/**
 * Пиктограммы режимов ЧС (дизайн Arena.ai, артборд 1): спокойные крупные
 * глифы — щит с человеком и «вниз в укрытие», дверь-выход со стрелкой.
 * Доверенные SVG-строки (рендер через dangerouslySetInnerHTML в панели).
 */

/** SHELTER: щит + человек + стрелка «вниз в укрытие». */
const SHELTER_SVG = `<svg width="50" height="50" viewBox="0 0 52 52" fill="none" aria-hidden="true">
<path d="M26 3L8 11v17c0 13.5 8 21.5 18 25 10-3.5 18-11.5 18-25V11L26 3z"
  fill="rgba(59,79,212,0.22)" stroke="url(#emShieldGrad)" stroke-width="2.2"/>
<circle cx="26" cy="20" r="4.5" fill="rgba(210,225,255,0.9)"/>
<path d="M17 35c0-4.8 4-8.8 9-8.8s9 4 9 8.8"
  stroke="rgba(210,225,255,0.9)" stroke-width="2.4" stroke-linecap="round"/>
<path d="M26 33v10M22 39l4 4 4-4" stroke="#00e5ff" stroke-width="2.2"
  stroke-linecap="round" stroke-linejoin="round"/>
<defs><linearGradient id="emShieldGrad" x1="8" y1="3" x2="44" y2="47" gradientUnits="userSpaceOnUse">
<stop stop-color="#60a5fa"/><stop offset="1" stop-color="#00e5ff"/></linearGradient></defs>
</svg>`

/** EVACUATION: проём-выход + человек + стрелка наружу. */
const EVACUATION_SVG = `<svg width="50" height="50" viewBox="0 0 52 52" fill="none" aria-hidden="true">
<rect x="8" y="6" width="22" height="40" rx="3"
  fill="rgba(16,185,129,0.14)" stroke="url(#emExitGrad)" stroke-width="2.2"/>
<circle cx="21" cy="18" r="4" fill="rgba(220,255,240,0.9)"/>
<path d="M14 34c0-4.2 3.2-7.4 7-7.4s7 3.2 7 7.4"
  stroke="rgba(220,255,240,0.9)" stroke-width="2.2" stroke-linecap="round"/>
<path d="M32 26h12M40 20l6 6-6 6" stroke="#34d399" stroke-width="2.4"
  stroke-linecap="round" stroke-linejoin="round"/>
<defs><linearGradient id="emExitGrad" x1="8" y1="6" x2="30" y2="46" gradientUnits="userSpaceOnUse">
<stop stop-color="#34d399"/><stop offset="1" stop-color="#10b981"/></linearGradient></defs>
</svg>`

/** Пиктограмма по режиму кадра (0=EVACUATION, 1=SHELTER, 2=DRILL). */
export function modePictogram(mode: number): string {
  return mode === 0 ? EVACUATION_SVG : SHELTER_SVG
}

/* ── Иконки шагов памятки (артборд 1: line-глифы, запрет — красная косая,
      акцент — циан; stroke currentColor наследует цвет карточки) ── */

const S = 'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"'
const SLASH =
  '<line x1="16.5" y1="3.5" x2="3.5" y2="16.5" stroke="#ff4d3a" stroke-width="1.8" stroke-linecap="round"/>'
const wrap = (body: string): string =>
  `<svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">${body}</svg>`

const I = {
  noLift: wrap(
    `<rect x="4" y="3" width="12" height="14" rx="1.5" ${S}/><path d="M8 8l2-2 2 2M8 12l2 2 2-2" ${S}/>${SLASH}`,
  ),
  exitDoor: wrap(
    `<rect x="3" y="3" width="9" height="14" rx="1.5" ${S}/><path d="M12 10h5M15 7.5l2.5 2.5-2.5 2.5" stroke="#34d399" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`,
  ),
  smokeLow: wrap(
    `<path d="M3 6c2-1.5 4 1.5 6 0s4 1.5 6 0" ${S}/><path d="M3 9.5c2-1.5 4 1.5 6 0s4 1.5 6 0" ${S}/><path d="M10 12.5v4M7.5 14.5l2.5 2.5 2.5-2.5" stroke="#00e5ff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  ),
  noBag: wrap(
    `<rect x="4" y="7" width="12" height="9" rx="2" ${S}/><path d="M7.5 7V5.5a2.5 2.5 0 0 1 5 0V7" ${S}/>${SLASH}`,
  ),
  assembly: wrap(
    `<path d="M5 17V4" ${S}/><path d="M5 4h9l-2.2 3L14 10H5" fill="rgba(0,229,255,0.15)" stroke="#00e5ff" stroke-width="1.6" stroke-linejoin="round"/>`,
  ),
  upstairs: wrap(
    `<path d="M3 16h4v-4h4V8h4V4h2" ${S}/><path d="M13 16l3-3M16 16v-3h-3" stroke="#34d399" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  ),
  unplug: wrap(
    `<path d="M11 3l-4.5 8H10l-1 6 4.5-8H10l1-6z" fill="rgba(255,205,60,0.15)" ${S}/>${SLASH}`,
  ),
  helpOthers: wrap(
    `<circle cx="7" cy="6" r="2.2" ${S}/><path d="M3.5 16c0-2.6 1.6-4.4 3.5-4.4s3.5 1.8 3.5 4.4" ${S}/><path d="M12.5 8.5c1.8.4 3.5 2 4 4.5" stroke="#34d399" stroke-width="1.6" stroke-linecap="round"/><circle cx="14.5" cy="5.5" r="1.8" stroke="#34d399" stroke-width="1.6"/>`,
  ),
  routeDots: wrap(
    `<circle cx="4.5" cy="15.5" r="1.8" ${S}/><circle cx="15.5" cy="4.5" r="1.8" stroke="#00e5ff" stroke-width="1.6"/><path d="M6 14c2.5-1 5.5-4 8-8" stroke-dasharray="2.6 2.6" ${S}/>`,
  ),
  swapShelter: wrap(
    `<path d="M7 3.5L3 5.5v3.8c0 3 1.8 4.8 4 5.7 2.2-.9 4-2.7 4-5.7V5.5L7 3.5z" ${S}/><path d="M13 12h4.5M15.5 9.5L18 12l-2.5 2.5" stroke="#00e5ff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  ),
  docsWater: wrap(
    `<rect x="4" y="3" width="9" height="12" rx="1.5" ${S}/><path d="M6.5 6.5h4M6.5 9h4M6.5 11.5h2.5" ${S}/><path d="M16 11c1.2 1.6 1.8 2.7 1.8 3.7a1.8 1.8 0 1 1-3.6 0c0-1 .6-2.1 1.8-3.7z" stroke="#00e5ff" stroke-width="1.5"/>`,
  ),
  follow: wrap(
    `<path d="M4 15l9-5.5L4 4v11z" fill="rgba(0,229,255,0.12)" ${S}/><path d="M14 6.5c1.8 1 2.8 2 3.3 3.5-.5 1.5-1.5 2.5-3.3 3.5" stroke="#00e5ff" stroke-width="1.5" stroke-linecap="round"/>`,
  ),
  shelterDown: wrap(
    `<path d="M10 2.5L4 5v4.5c0 4 2.4 6.5 6 8 3.6-1.5 6-4 6-8V5l-6-2.5z" ${S}/><path d="M10 7v6M7.5 10.5L10 13l2.5-2.5" stroke="#00e5ff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  ),
  noWindow: wrap(
    `<rect x="3.5" y="3.5" width="13" height="13" rx="1.5" ${S}/><path d="M10 3.5v13M3.5 10h13" ${S}/>${SLASH}`,
  ),
  noGeo: wrap(
    `<path d="M10 3a4.5 4.5 0 0 1 4.5 4.5C14.5 11 10 16 10 16s-4.5-5-4.5-8.5A4.5 4.5 0 0 1 10 3z" ${S}/><circle cx="10" cy="7.5" r="1.6" ${S}/>${SLASH}`,
  ),
  waitClear: wrap(
    `<circle cx="10" cy="10" r="7" ${S}/><path d="M10 6v4l2.8 1.6" stroke="#00e5ff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  ),
  phoneSilent: wrap(
    `<rect x="6" y="3" width="8" height="14" rx="2" ${S}/><path d="M9 14.5h2" ${S}/>${SLASH}`,
  ),
}

/** Иконки шагов по типу опасности — индексы совпадают с drill.{hazard}.stepN. */
export const MEMO_ICONS: Readonly<Record<string, readonly string[]>> = {
  fire: [I.noLift, I.exitDoor, I.smokeLow, I.noBag, I.assembly],
  flood: [I.upstairs, I.noLift, I.unplug, I.helpOthers],
  evac: [I.routeDots, I.swapShelter, I.docsWater, I.follow],
  uav: [I.shelterDown, I.noWindow, I.noGeo, I.waitClear, I.phoneSilent],
}
